"use client"

import { router } from "@inertiajs/react"

import { UserStatusBadge } from "@/components/admin/users/status-badge"
import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Table, TableCard } from "@/components/ui/table"
import { formatDate } from "@/lib/utils/"
import { adminUserPath } from "@/routes"
import type { UserRow } from "@/types"

interface UsersTableProps {
  rows: UserRow[]
  "aria-label"?: string
  footer?: React.ReactNode
}

// The admin userbase table. Numeric columns are right-aligned tabular
// figures; the whole row opens the user's support page.
export function UsersTable({
  rows,
  "aria-label": ariaLabel = "Users",
  footer,
}: UsersTableProps) {
  return (
    <TableCard.Root>
      <Table aria-label={ariaLabel} className="bg-primary">
        <Table.Header>
          <Table.Head id="user" isRowHeader label="User" className="w-full" />
          <Table.Head id="status" label="Status" />
          <Table.Head id="playlists" label="Playlists" className="text-right" />
          <Table.Head
            id="suggestions"
            label="Suggestions"
            className="text-right"
          />
          <Table.Head id="joined" label="Joined" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: UserRow) => (
            <Table.Row
              id={row.id}
              className="cursor-pointer"
              onAction={() => router.visit(adminUserPath(row.id))}
            >
              <Table.Cell>
                <div className="flex items-center gap-3">
                  <Avatar
                    name={row.name}
                    avatarUrl={row.avatar_url}
                    size="sm"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-primary text-sm font-medium whitespace-nowrap">
                        {row.name}
                      </p>
                      {row.admin && (
                        <Badge color="brand" size="sm" type="pill-color">
                          Admin
                        </Badge>
                      )}
                    </div>
                    <p className="text-tertiary text-sm whitespace-nowrap">
                      {row.email}
                    </p>
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell>
                <UserStatusBadge status={row.status} />
              </Table.Cell>
              <Table.Cell className="text-tertiary text-right whitespace-nowrap tabular-nums">
                {row.published_playlists_count}/{row.playlists_count}
              </Table.Cell>
              <Table.Cell className="text-tertiary text-right tabular-nums">
                {row.suggestions_count}
              </Table.Cell>
              <Table.Cell className="text-tertiary whitespace-nowrap">
                {formatDate(row.created_at)}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      {footer}
    </TableCard.Root>
  )
}
